import type { TimeEntryDto, TimeEntryParsePreview } from '@fos/shared/contracts/time';
import { computeEntryAmount } from '@fos/shared/money';
import { addDaysLocal, todayLocal } from '@fos/shared/time';
import { keepPreviousData } from '@tanstack/react-query';
import { MessageSquareText, Plus } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { formatMoney, formatRate } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button, type ButtonProps } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { DateInput, TimeInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE, TOUCH_BUTTON } from '../../mobile';
import { showWarnings, timeErrorToast, useInvalidateTime } from '../hooks/useTimeMutations';
import { comboKey, splitComboKey, useCombos, useTimeOptions } from '../hooks/useTimeOptions';
import { endFromDuration, parseDurationInput, previewTiming } from '../time-format';
import { LinkPicker } from './LinkPicker';
import { SentenceEntry } from './SentenceEntry';

/**
 * Dialog „Dodaj czas” / „Edytuj wpis” (MVP §7, DECISIONS: wpis ręczny od–do, bez stopera): opis, klient · projekt,
 * usługa, zadanie, data, od, do albo czas trwania (koniec liczony), dzień końca przy przejściu przez północ,
 * „Rozliczany”, stawka (tylko „Widzi stawki i kwoty”), osoba (Manager / Admin). Ten sam formularz na desktopie
 * i mobile. Opcjonalnie wpis zdaniem (`SentenceEntry`). Czas sprawdza `resolveEntryTiming` (podgląd) i serwer.
 */

const NONE = '__brak__';

type FieldName =
  | 'description'
  | 'combo'
  | 'serviceId'
  | 'taskId'
  | 'date'
  | 'start'
  | 'end'
  | 'endDate'
  | 'duration'
  | 'billable'
  | 'rate'
  | 'userId';

const PATH_TO_FIELD: Record<string, FieldName> = {
  description: 'description',
  clientId: 'combo',
  projectId: 'combo',
  serviceId: 'serviceId',
  taskId: 'taskId',
  date: 'date',
  start: 'start',
  end: 'end',
  endDate: 'endDate',
  durationSeconds: 'duration',
  billable: 'billable',
  rate: 'rate',
  currency: 'rate',
  userId: 'userId',
};

interface Values {
  description: string;
  combo: string;
  serviceId: string | null;
  taskId: string | null;
  date: string;
  start: string;
  end: string;
  endDate: string | null;
  durationText: string;
  billable: boolean;
  billableTouched: boolean;
  rateText: string;
  userId: string;
}

export interface AddTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** wartości domyślne nowego wpisu (profil klienta, projekt, zadanie) */
  defaultClientId?: string | null;
  defaultProjectId?: string | null;
  defaultTaskId?: string | null;
  defaultDate?: string;
  /** edycja istniejącego wpisu */
  entry?: TimeEntryDto | null;
  /** duplikat: wartości wpisu, zapis jako nowy wpis */
  duplicateOf?: TimeEntryDto | null;
  /** od razu wpis zdaniem */
  initialMode?: 'form' | 'sentence';
  initialSentence?: string;
  onSaved?: (entry: TimeEntryDto) => void;
}

const normalizeRate = (text: string) => text.trim().replace(',', '.').replace(/\s/g, '');

function fromEntry(e: TimeEntryDto, opts: { duplicate: boolean; today: string }): Values {
  const date = opts.duplicate ? opts.today : e.date;
  return {
    description: e.description,
    combo: comboKey(e.clientId, e.projectId),
    serviceId: e.serviceId,
    taskId: e.taskId,
    date,
    start: e.start,
    end: e.end,
    endDate: e.endDate ? (opts.duplicate ? addDaysLocal(date, 1) : e.endDate) : null,
    durationText: '',
    billable: e.billable,
    billableTouched: opts.duplicate,
    rateText: e.rate ?? '',
    userId: e.userId,
  };
}

/** Dialog wpisu czasu. Eksport dla innych widoków: props `defaultClientId?`, `defaultProjectId?`, `defaultTaskId?`. */
export function AddTimeDialog({
  open,
  onOpenChange,
  defaultClientId,
  defaultProjectId,
  defaultTaskId,
  defaultDate,
  entry,
  duplicateOf,
  initialMode = 'form',
  initialSentence,
  onSaved,
}: AddTimeDialogProps) {
  const session = useActiveSession();
  const access = useAccess();
  const me = session.user.id;
  const today = todayLocal();
  const editing = Boolean(entry);
  const seesRates = access.flag('seesRates');
  const canTasks = access.can('tasks');
  const options = useTimeOptions(open);
  const data = options.data;
  const combos = useCombos(data);
  const create = trpc.time.create.useMutation();
  const update = trpc.time.update.useMutation();
  const invalidate = useInvalidateTime();

  const [mode, setMode] = useState<'form' | 'sentence'>(initialMode);
  const [values, setValues] = useState<Values>(() => blank());
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);
  const dirty = useRef(false);
  const wasOpen = useRef(false);

  function blank(): Values {
    return {
      description: '',
      combo: comboKey(defaultClientId ?? null, defaultProjectId ?? null),
      serviceId: null,
      taskId: defaultTaskId ?? null,
      date: defaultDate ?? today,
      start: '',
      end: '',
      endDate: null,
      durationText: '',
      billable: false,
      billableTouched: false,
      rateText: '',
      userId: me,
    };
  }

  // poprzednie dane zostają przy zmianie klienta (efekt domyślnych wartości reaguje tylko na pierwsze wczytanie)
  const taskOptions = trpc.time.taskOptions.useQuery(splitComboKey(values.combo), {
    enabled: open && canTasks,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  // Świeże wartości przy otwarciu; po wczytaniu słowników uzupełniamy domyślne połączenie (bez nadpisywania zmian).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu i po wczytaniu słowników
  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      return;
    }
    if (wasOpen.current && dirty.current) return;
    if (!wasOpen.current) {
      dirty.current = false;
      setMode(initialMode);
      setErrors({});
      setFormError(null);
    }
    wasOpen.current = true;
    const source = entry ?? duplicateOf;
    let next = source ? fromEntry(source, { duplicate: !entry, today }) : blank();
    if (!source && data) {
      next = withDefaultLinks(next);
    }
    setValues(withDuration(next));
  }, [open, data === undefined, taskOptions.data === undefined]);

  /** Domyślne połączenie: projekt / klient / zadanie z props, inaczej ostatnio używane. */
  function withDefaultLinks(v: Values): Values {
    let clientId = defaultClientId ?? null;
    let projectId = defaultProjectId ?? null;
    if (defaultTaskId) {
      const task = taskOptions.data?.find((t) => t.id === defaultTaskId);
      if (task) {
        clientId = task.clientId;
        projectId = task.projectId ?? projectId;
      }
    }
    if (projectId) clientId = data?.projects.find((p) => p.id === projectId)?.clientId ?? clientId;
    if (!clientId && !projectId && !defaultTaskId && data?.recent[0]) {
      clientId = data.recent[0].clientId;
      projectId = data.recent[0].projectId;
    }
    const combo = comboKey(clientId, projectId);
    return { ...v, combo, billable: defaultBillable(combo) };
  }

  function defaultBillable(combo: string): boolean {
    const { clientId, projectId } = splitComboKey(combo);
    if (!clientId) return false;
    const project = projectId ? data?.projects.find((p) => p.id === projectId) : undefined;
    return project?.billableDefault ?? true;
  }

  function withDuration(v: Values): Values {
    const p = previewTiming(v);
    return { ...v, durationText: p.seconds !== null ? clockText(p.seconds) : v.durationText };
  }

  const set = <K extends keyof Values>(key: K, value: Values[K], clear: FieldName[] = []) => {
    dirty.current = true;
    setValues((v) => {
      const next = { ...v, [key]: value };
      return key === 'date' || key === 'start' || key === 'end' || key === 'endDate' ? withDuration(next) : next;
    });
    setErrors((e) => {
      const out = { ...e };
      for (const f of clear) delete out[f];
      return out;
    });
  };

  const setCombo = (key: string) => {
    dirty.current = true;
    setValues((v) => {
      const { clientId } = splitComboKey(key);
      const service = data?.services.find((s) => s.id === v.serviceId);
      const task = taskOptions.data?.find((t) => t.id === v.taskId);
      return {
        ...v,
        combo: key,
        serviceId: service && service.clientId === clientId ? v.serviceId : null,
        taskId: task && task.clientId === clientId ? v.taskId : null,
        billable: v.billableTouched ? v.billable && Boolean(clientId) : defaultBillable(key),
      };
    });
    setErrors((e) => ({ ...e, combo: undefined, serviceId: undefined, taskId: undefined, billable: undefined }));
  };

  const applyDuration = () => {
    const seconds = parseDurationInput(values.durationText);
    if (values.durationText.trim() === '') return;
    if (seconds === null || seconds <= 0) {
      setErrors((e) => ({ ...e, duration: 'Wpisz czas, np. 1:40 albo 1h40' }));
      return;
    }
    if (!values.start) {
      setErrors((e) => ({ ...e, start: 'Podaj godzinę początku' }));
      return;
    }
    const end = endFromDuration(values.date, values.start, seconds);
    if (!end) {
      setErrors((e) => ({ ...e, duration: 'Wpis nie może być dłuższy niż 24 h' }));
      return;
    }
    dirty.current = true;
    setValues((v) => withDuration({ ...v, end: end.end, endDate: end.endDate }));
    setErrors((e) => ({ ...e, duration: undefined, end: undefined, endDate: undefined }));
  };

  const timing = previewTiming(values);
  const { clientId } = splitComboKey(values.combo);
  const client = clientId ? data?.clients.find((c) => c.id === clientId) : undefined;
  // klient widoczny tylko przez projekt (np. partner we wspólnym projekcie): „rozliczany” i stawkę klienta ustala
  // prowadzący — wpis bierze ustawienie projektu (DECISIONS 2026-10-08 [P] „Stawka partnera”)
  const partialClient = Boolean(client?.partial);
  const showRate = seesRates && !partialClient;
  const services = useMemo(() => (data?.services ?? []).filter((s) => s.clientId === clientId), [data, clientId]);
  const serviceOptions: ComboboxOption[] = [
    { value: NONE, label: 'Bez usługi' },
    ...services.map((s) => ({ value: s.id, label: s.name })),
  ];
  const taskList = taskOptions.data ?? [];
  const taskChoices: ComboboxOption[] = [
    { value: NONE, label: 'Bez zadania' },
    ...taskList.map((t) => ({ value: t.id, label: `#${t.number} ${t.title}` })),
  ];
  const memberOptions: ComboboxOption[] = (data?.members ?? []).map((m) => ({
    value: m.userId,
    label: m.userId === me ? `${m.name} (Ty)` : m.name,
  }));
  const rateForAmount = showRate
    ? values.rateText.trim()
      ? normalizeRate(values.rateText)
      : editing
        ? null
        : (client?.defaultRate ?? null)
    : null;
  const amountPreview =
    showRate && timing.seconds !== null && /^\d{1,8}(\.\d{1,2})?$/.test(rateForAmount ?? '0')
      ? computeEntryAmount({ rate: rateForAmount, seconds: timing.seconds, billable: values.billable })
      : null;

  // waluta kwoty: wpis zachowuje swoją (snapshot), nowy wpis i zmiana klienta — waluta klienta
  const amountCurrency =
    entry && entry.clientId === (client?.id ?? null) ? entry.currency : (client?.currency ?? entry?.currency ?? 'PLN');
  const readOnly = Boolean(entry && (!entry.canEdit || entry.closed));
  const close = () => onOpenChange(false);

  const fail = (err: unknown) => {
    const fieldErrors: Partial<Record<FieldName, string>> = {};
    for (const issue of validationIssues(err)) {
      const key = PATH_TO_FIELD[issue.path.split('.')[0] ?? ''];
      if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
    }
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length === 0) setFormError(errorMessage(err, 'Nie udało się zapisać wpisu.'));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (readOnly) return;
    setFormError(null);
    const local: Partial<Record<FieldName, string>> = {};
    if (!values.date) local.date = 'Podaj datę';
    if (!values.start) local.start = 'Podaj godzinę początku';
    if (!values.end) local.end = 'Podaj godzinę końca albo czas';
    if (Object.keys(local).length === 0 && timing.error) {
      local[(PATH_TO_FIELD[timing.errorPath ?? 'end'] ?? 'end') as FieldName] = timing.needsEndDate
        ? 'Wpis przez północ: wskaż dzień zakończenia'
        : timing.error;
    }
    if (seesRates && values.rateText.trim() && !/^\d{1,8}(\.\d{1,2})?$/.test(normalizeRate(values.rateText))) {
      local.rate = 'Wpisz stawkę, np. 150 albo 150,50';
    }
    if (Object.keys(local).length > 0) {
      setErrors(local);
      return;
    }
    const links = splitComboKey(values.combo);
    try {
      if (!entry) {
        const result = await create.mutateAsync({
          description: values.description.trim(),
          clientId: links.clientId,
          projectId: links.projectId,
          serviceId: values.serviceId,
          taskId: values.taskId,
          date: values.date,
          start: values.start,
          end: values.end,
          endDate: values.endDate,
          ...(values.billableTouched && !partialClient ? { billable: values.billable } : {}),
          ...(showRate && values.rateText.trim() ? { rate: normalizeRate(values.rateText) } : {}),
          ...(data?.canChooseUser && values.userId !== me ? { userId: values.userId } : {}),
        });
        await invalidate();
        close();
        showWarnings(result.warnings);
        if (result.warnings.length === 0) {
          toast.success('Dodano czas', { description: result.entry.description || undefined });
        }
        onSaved?.(result.entry);
        return;
      }
      const before = fromEntry(entry, { duplicate: false, today });
      const patch: Record<string, unknown> = {};
      if (values.description.trim() !== before.description) patch.description = values.description.trim();
      if (values.combo !== before.combo) Object.assign(patch, links);
      if (values.serviceId !== before.serviceId) patch.serviceId = values.serviceId;
      if (values.taskId !== before.taskId) patch.taskId = values.taskId;
      if (
        values.date !== before.date ||
        values.start !== before.start ||
        values.end !== before.end ||
        values.endDate !== before.endDate
      ) {
        Object.assign(patch, { date: values.date, start: values.start, end: values.end, endDate: values.endDate });
      }
      if (!partialClient && values.billable !== before.billable) patch.billable = values.billable;
      if (showRate && normalizeRate(values.rateText) !== normalizeRate(before.rateText)) {
        patch.rate = values.rateText.trim() ? normalizeRate(values.rateText) : null;
      }
      if (values.userId !== before.userId) patch.userId = values.userId;
      if (Object.keys(patch).length === 0) {
        close();
        return;
      }
      const result = await update.mutateAsync({ id: entry.id, version: entry.version, ...patch });
      await invalidate();
      close();
      showWarnings(result.warnings);
      if (result.warnings.length === 0) toast.success('Zapisano zmiany');
      onSaved?.(result.entry);
    } catch (err) {
      if (validationIssues(err).length > 0) {
        fail(err);
        return;
      }
      if (entry) {
        await invalidate();
        timeErrorToast(err);
        close();
        return;
      }
      fail(err);
    }
  };

  const pending = create.isPending || update.isPending;
  const title = editing ? 'Edytuj wpis' : 'Dodaj czas';

  if (mode === 'sentence' && !editing) {
    return (
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        width="l"
        title="Nowy wpis czasu"
        className={FULLSCREEN_ON_PHONE}
        description="Wpisz zdaniem, np. „HelpMyCar 8:30–16:30 frontend”. Sprawdź podgląd i zapisz."
      >
        <SentenceEntry
          initialText={initialSentence}
          onCancel={close}
          onSaved={close}
          onManual={(preview) => {
            if (preview) setValues((v) => withDuration(fromPreview(v, preview)));
            dirty.current = true;
            setMode('form');
          }}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={title}
      className={FULLSCREEN_ON_PHONE}
      footer={
        readOnly ? (
          <Button variant="secondary" onClick={close}>
            Zamknij
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Anuluj
            </Button>
            <Button variant="primary" type="submit" form="time-entry-form" loading={pending}>
              {editing ? 'Zapisz' : 'Dodaj czas'}
            </Button>
          </>
        )
      }
    >
      <form id="time-entry-form" ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {readOnly && (
          <FormMessage>
            {entry?.closed
              ? 'Wpis jest zamknięty w rozliczeniu. Korekta wymaga osobnego procesu.'
              : 'Możesz tylko podejrzeć ten wpis.'}
          </FormMessage>
        )}
        {formError && <FormMessage>{formError}</FormMessage>}
        {options.isError && (
          <FormMessage>
            Nie udało się wczytać klientów i projektów.{' '}
            <button
              type="button"
              className="font-medium text-accent hover:underline"
              onClick={() => void options.refetch()}
            >
              Spróbuj ponownie
            </button>
          </FormMessage>
        )}
        {!editing && (
          <button
            type="button"
            onClick={() => setMode('sentence')}
            className="inline-flex items-center gap-1.5 self-start rounded-xs text-caption text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
          >
            <MessageSquareText size={14} strokeWidth={1.75} aria-hidden />
            Możesz też wpisać zdaniem
          </button>
        )}
        <fieldset disabled={readOnly} className="flex flex-col gap-4">
          <Field label="Nad czym pracowałeś?" error={errors.description}>
            <Input
              autoFocus
              value={values.description}
              onChange={(e) => set('description', e.target.value, ['description'])}
              placeholder="np. Frontend development"
              maxLength={500}
            />
          </Field>
          <Field label="Klient · projekt" error={errors.combo}>
            <LinkPicker
              combos={combos}
              value={values.combo}
              onValueChange={setCombo}
              loading={options.isPending}
              disabled={readOnly}
            />
          </Field>
          {(services.length > 0 || (canTasks && taskList.length > 0) || values.serviceId || values.taskId) && (
            <div className="grid gap-4 sm:grid-cols-2">
              {(services.length > 0 || values.serviceId) && (
                <Field label="Usługa" error={errors.serviceId}>
                  <Combobox
                    value={values.serviceId ?? NONE}
                    onValueChange={(v) => set('serviceId', v === NONE ? null : v, ['serviceId'])}
                    options={serviceOptions}
                    searchPlaceholder="Szukaj usługi…"
                    disabled={readOnly}
                  />
                </Field>
              )}
              {canTasks && (taskList.length > 0 || values.taskId) && (
                <Field label="Zadanie" error={errors.taskId}>
                  <Combobox
                    value={values.taskId ?? NONE}
                    onValueChange={(v) => set('taskId', v === NONE ? null : v, ['taskId'])}
                    options={
                      values.taskId && !taskList.some((t) => t.id === values.taskId) && entry?.task
                        ? [...taskChoices, { value: entry.task.id, label: `#${entry.task.number} ${entry.task.title}` }]
                        : taskChoices
                    }
                    searchPlaceholder="Szukaj zadania…"
                    disabled={readOnly}
                  />
                </Field>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-[1.6fr_1fr_1fr_1fr]">
            <Field label="Data" error={errors.date} className="col-span-2 sm:col-span-1">
              <DateInput
                value={values.date}
                onValueChange={(v) => {
                  set('date', v, ['date', 'end', 'endDate']);
                  // dzień końca idzie za datą (wpis przez północ kończy się następnego dnia)
                  if (values.endDate && v) set('endDate', addDaysLocal(v, 1));
                }}
              />
            </Field>
            <Field label="Od" error={errors.start}>
              <TimeInput
                size="m"
                className="w-full"
                value={values.start}
                onValueChange={(v) => set('start', v, ['start', 'end', 'endDate'])}
              />
            </Field>
            <Field label="Do" error={errors.end}>
              <TimeInput
                size="m"
                className="w-full"
                value={values.end}
                onValueChange={(v) => set('end', v, ['end', 'endDate', 'duration'])}
              />
            </Field>
            <Field label="Czas" error={errors.duration} className="col-span-2 sm:col-span-1">
              <Input
                mono
                inputMode="text"
                autoComplete="off"
                value={values.durationText}
                onChange={(e) => {
                  const text = e.target.value;
                  setValues((v) => ({ ...v, durationText: text }));
                }}
                onBlur={applyDuration}
                placeholder="1:40"
                maxLength={12}
              />
            </Field>
          </div>
          {(timing.needsEndDate || values.endDate) && (
            <Field
              label="Dzień końca"
              error={errors.endDate ?? (timing.needsEndDate ? 'Wpis przez północ: wskaż dzień zakończenia' : null)}
              hint="Wpis przechodzi przez północ"
            >
              <div className="flex flex-wrap items-center gap-2">
                {values.endDate ? (
                  <>
                    <DateInput
                      className="max-w-[200px]"
                      value={values.endDate}
                      onValueChange={(v) => set('endDate', v || null, ['endDate', 'end'])}
                    />
                    <Button variant="ghost" size="s" onClick={() => set('endDate', null, ['endDate'])}>
                      Ten sam dzień
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="secondary"
                    size="s"
                    onClick={() => set('endDate', addDaysLocal(values.date, 1), ['endDate', 'end'])}
                  >
                    Koniec następnego dnia
                  </Button>
                )}
              </div>
            </Field>
          )}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex min-h-10 flex-1 flex-col justify-center gap-1">
              <Checkbox
                checked={values.billable}
                disabled={!clientId || readOnly || partialClient}
                onCheckedChange={(checked) => {
                  dirty.current = true;
                  setValues((v) => ({ ...v, billable: checked, billableTouched: true }));
                }}
                label="Rozliczany"
              />
              <span className="text-caption text-fg-muted">
                {partialClient
                  ? 'Ustawienie projektu — zmienia je prowadzący.'
                  : clientId
                    ? 'Trafi do zestawienia dla klienta.'
                    : 'Bez klienta wpis jest nierozliczany.'}
              </span>
              {errors.billable && <span className="text-caption text-danger">{errors.billable}</span>}
            </div>
            {showRate && (
              <Field
                label="Stawka"
                error={errors.rate}
                className="sm:w-[200px]"
                hint={
                  editing
                    ? 'Puste = bez stawki'
                    : client?.defaultRate
                      ? `Puste = stawka klienta (${formatRate(client.defaultRate, client.currency)})`
                      : clientId
                        ? 'Klient nie ma stawki'
                        : undefined
                }
              >
                <Input
                  mono
                  inputMode="decimal"
                  value={values.rateText}
                  onChange={(e) => set('rateText', e.target.value, ['rate'])}
                  placeholder={client?.defaultRate ?? '0'}
                  disabled={!clientId || readOnly}
                />
              </Field>
            )}
          </div>
          {data?.canChooseUser && (
            <Field label="Osoba" error={errors.userId}>
              <Combobox
                value={values.userId}
                onValueChange={(v) => set('userId', v, ['userId'])}
                options={memberOptions}
                searchPlaceholder="Szukaj osoby…"
                disabled={readOnly}
              />
            </Field>
          )}
          {amountPreview !== null && values.billable && (
            <p className="text-body-s text-fg-secondary">
              Kwota: <Mono className="text-fg">{formatMoney(amountPreview, amountCurrency)}</Mono>
            </p>
          )}
          {entry && entry.userId === me && entry.partnerRate && (
            <p className="text-body-s text-fg-secondary">
              Moja stawka: <Mono className="text-fg">{formatRate(entry.partnerRate, 'PLN')}</Mono> · Moja kwota:{' '}
              <Mono className="text-fg">{formatMoney(entry.partnerAmount ?? '0.00', 'PLN')}</Mono>
              {entry.partnerSettlementId ? ' · rozliczone z prowadzącym' : ''}
            </p>
          )}
        </fieldset>
      </form>
    </Dialog>
  );
}

function clockText(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Wartości z podglądu zdania („Wpisz ręcznie”). */
function fromPreview(v: Values, p: TimeEntryParsePreview): Values {
  return {
    ...v,
    description: p.description || v.description,
    combo: p.client ? comboKey(p.client.id, p.project?.id ?? null) : v.combo,
    date: p.date,
    start: p.start ?? v.start,
    end: p.end ?? v.end,
    endDate: p.endDate,
  };
}

/** Przycisk „Dodaj czas” z własnym dialogiem (profil klienta, projekt, zadanie). */
export function AddTimeButton({
  defaultClientId,
  defaultProjectId,
  defaultTaskId,
  label = 'Dodaj czas',
  variant = 'secondary',
  size,
}: {
  defaultClientId?: string | null;
  defaultProjectId?: string | null;
  defaultTaskId?: string | null;
  label?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
}) {
  const access = useAccess();
  const [open, setOpen] = useState(false);
  if (!access.can('time', 'edit')) return null;
  return (
    <>
      <Button variant={variant} size={size} icon={Plus} onClick={() => setOpen(true)} className={TOUCH_BUTTON}>
        {label}
      </Button>
      <AddTimeDialog
        open={open}
        onOpenChange={setOpen}
        defaultClientId={defaultClientId}
        defaultProjectId={defaultProjectId}
        defaultTaskId={defaultTaskId}
      />
    </>
  );
}
